import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import * as EventConstants from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SavingState} from 'constants/RegistryConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {Schema} from 'schemas/SchemaConstants';
import APIUtil from 'util/APIUtil';

export function updateSchemas<T extends Schema>(
  existingSchemas: Array<T> | null,
  schemasToMerge: Array<T>
): Array<T> {
  if (!existingSchemas) {
    return schemasToMerge;
  }
  const existingSchemasById = _.indexBy(existingSchemas, 'id');
  for (const schema of schemasToMerge) {
    existingSchemasById[schema.id] = schema;
  }
  return Object.values(existingSchemasById);
}

class SampleSchemaStore extends Store {
  savingState!: SavingState;
  loadingTagSchemas!: {[key: string]: boolean};
  boxSchemas!: Array<BoxSchema> | null;
  containerSchemas!: Array<ContainerSchema> | null;
  locationSchemas!: Array<LocationSchema> | null;
  plateSchemas!: Array<PlateSchema> | null;

  resetState(): void {
    this.loadingTagSchemas = {};
    this.savingState = SavingState.COMPLETED;
    // These include archived storable schemas; the getter functions filter them out and should be used to
    // read from the store everywhere in the UI except for the schema settings pages
    this.boxSchemas = null;
    this.containerSchemas = null;
    this.locationSchemas = null;
    this.plateSchemas = null;
  }

  _stateKeyForStorableSchemaClass(
    schemaClass: string
  ): 'plateSchemas' | 'boxSchemas' | 'containerSchemas' | 'locationSchemas' {
    switch (schemaClass) {
      case ModelTypeConstants.BOX_SCHEMA:
        return 'boxSchemas';
      case ModelTypeConstants.CONTAINER_SCHEMA:
        return 'containerSchemas';
      case ModelTypeConstants.LOCATION_SCHEMA:
        return 'locationSchemas';
      case ModelTypeConstants.PLATE_SCHEMA:
        return 'plateSchemas';
      default:
        assert(false, 'Unexpected schema class');
        return null as never;
    }
  }

  getSavingState(): SavingState {
    return this.savingState;
  }

  setSavingState(savingState: SavingState): void {
    this.savingState = savingState;
  }

  /**
   * If registryId is not supplied, will return results for all schemas.
   */
  _getStorableSchemas(
    registryId: string | null,
    schemaClass: string,
    {includeArchived}: {includeArchived: boolean}
  ): Array<ContainerSchema | PlateSchema | BoxSchema> | null {
    const stateKey = this._stateKeyForStorableSchemaClass(schemaClass);
    // If schemas have not been fetched and are loading, return null.
    if (!this[stateKey]) {
      return null;
    }
    return this[stateKey].filter(
      (schema) =>
        (!registryId || schema.registryId === registryId) && (includeArchived || !isArchived(schema))
    );
  }

  getBoxSchemas(
    registryId?: string | null,
    {includeArchived}: {includeArchived: boolean} = {includeArchived: false}
  ): Array<BoxSchema> | null {
    return this._getStorableSchemas(registryId || null, ModelTypeConstants.BOX_SCHEMA, {
      includeArchived,
    }) as Array<BoxSchema> | null;
  }

  getBoxSchema(boxSchemaId: string): BoxSchema | null {
    if (!this.boxSchemas) {
      return null;
    }
    return this.boxSchemas.find(({id}) => id === boxSchemaId) || null;
  }

  getContainerSchemas(
    registryId?: string | null,
    {includeArchived}: {includeArchived: boolean} = {includeArchived: false}
  ): Array<ContainerSchema> | null {
    return this._getStorableSchemas(registryId || null, ModelTypeConstants.CONTAINER_SCHEMA, {
      includeArchived,
    }) as Array<ContainerSchema> | null;
  }

  getContainerSchema(containerSchemaId: string | null): ContainerSchema | null {
    if (!this.containerSchemas) {
      return null;
    }
    return this.containerSchemas.find(({id}) => id === containerSchemaId) || null;
  }

  getLocationSchemas(
    registryId?: string | null,
    {includeArchived}: {includeArchived: boolean} = {includeArchived: false}
  ): Array<LocationSchema> | null {
    return this._getStorableSchemas(registryId || null, ModelTypeConstants.LOCATION_SCHEMA, {
      includeArchived,
    }) as Array<LocationSchema> | null;
  }

  getLocationSchema(locationSchemaId: string): LocationSchema | null {
    if (!this.locationSchemas) {
      return null;
    }
    return this.locationSchemas.find(({id}) => id === locationSchemaId) || null;
  }

  getPlateSchemas(
    registryId?: string | null,
    {includeArchived}: {includeArchived: boolean} = {includeArchived: false}
  ): Array<PlateSchema> | null {
    return this._getStorableSchemas(registryId || null, ModelTypeConstants.PLATE_SCHEMA, {
      includeArchived,
    }) as Array<PlateSchema> | null;
  }

  getPlateSchema(plateSchemaId: string): PlateSchema | null {
    if (!this.plateSchemas) {
      return null;
    }
    return this.plateSchemas.find(({id}) => id === plateSchemaId) || null;
  }

  getGridSchemas(
    registryId: string | null,
    schemaClass: string,
    {includeArchived}: {includeArchived: boolean} = {includeArchived: false}
  ): Array<BoxSchema | PlateSchema> | null {
    return this._getStorableSchemas(registryId, schemaClass, {includeArchived}) as Array<
      BoxSchema | PlateSchema
    > | null;
  }

  getGridSchema(gridSchemaId: string, schemaClass: string): BoxSchema | PlateSchema | null {
    switch (schemaClass) {
      case ModelTypeConstants.BOX_SCHEMA:
        return this.getBoxSchema(gridSchemaId);
      case ModelTypeConstants.PLATE_SCHEMA:
        return this.getPlateSchema(gridSchemaId);
      default:
        return null;
    }
  }

  getStorableSchemasByType(
    registryId?: string | null,
    {includeArchived}: {includeArchived: boolean} = {includeArchived: false}
  ): {
    containerSchemas: Array<ContainerSchema> | null;
    plateSchemas: Array<PlateSchema> | null;
    boxSchemas: Array<BoxSchema> | null;
    locationSchemas: Array<LocationSchema> | null;
  } {
    return {
      containerSchemas: this.getContainerSchemas(registryId, {includeArchived}),
      plateSchemas: this.getPlateSchemas(registryId, {includeArchived}),
      boxSchemas: this.getBoxSchemas(registryId, {includeArchived}),
      locationSchemas: this.getLocationSchemas(registryId, {includeArchived}),
    };
  }

  getStorableSchemaById(schemaId: string): BoxSchema | ContainerSchema | LocationSchema | PlateSchema | null {
    switch (APIUtil.apiIdToType(schemaId)) {
      case ModelTypeConstants.BOX_SCHEMA:
        return this.getBoxSchema(schemaId);
      case ModelTypeConstants.CONTAINER_SCHEMA:
        return this.getContainerSchema(schemaId);
      case ModelTypeConstants.LOCATION_SCHEMA:
        return this.getLocationSchema(schemaId);
      case ModelTypeConstants.PLATE_SCHEMA:
        return this.getPlateSchema(schemaId);
      default:
        return null;
    }
  }

  isTagSchemaLoading(tagSchemaId: string): boolean {
    return Boolean(this.loadingTagSchemas[tagSchemaId]) || false;
  }

  _setTagSchemaLoading(tagSchemaId: string, loading: boolean): void {
    this.loadingTagSchemas = reactUpdate(this.loadingTagSchemas, {
      [tagSchemaId]: {$set: loading},
    });
  }

  _updateStorableSchema(updatedSchema: StorableSchema): void {
    const stateKey = this._stateKeyForStorableSchemaClass(APIUtil.apiIdToType(updatedSchema.id));
    // eslint-disable-next-line no-restricted-syntax
    const index = _.findIndex(this[stateKey] as Array<StorableSchema>, ({id}) => id === updatedSchema.id);
    assert(index > -1, 'Expected schema to exist in the store.');
    (this[stateKey] as Array<StorableSchema> | null) = reactUpdate(this[stateKey], {
      $merge: {[index]: updatedSchema},
    });
  }

  _archiveStorableSchema(schemaId: string, archiveRecord: ArchiveRecord): void {
    const oldSchema = this.getStorableSchemaById(schemaId);
    if (oldSchema) {
      this._updateStorableSchema({...oldSchema, archiveRecord});
    }
  }

  _unarchiveStorableSchema(schemaId: string): void {
    const oldSchema = this.getStorableSchemaById(schemaId);
    if (oldSchema) {
      this._updateStorableSchema({...oldSchema, archiveRecord: null});
    }
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.BOX_SCHEMA_UPDATE:
      case EventConstants.CONTAINER_SCHEMA_UPDATE:
      case EventConstants.LOCATION_SCHEMA_UPDATE:
      case EventConstants.PLATE_SCHEMA_UPDATE: {
        this.setSavingState(SavingState.PENDING);
        this.emitChange();
        break;
      }
      case EventConstants.BOX_SCHEMA_UPDATE_FAILED:
      case EventConstants.CONTAINER_SCHEMA_UPDATE_FAILED:
      case EventConstants.LOCATION_SCHEMA_UPDATE_FAILED:
      case EventConstants.PLATE_SCHEMA_UPDATE_FAILED: {
        this.setSavingState(SavingState.FAILURE);
        this.emitChange();
        break;
      }
      case EventConstants.BOX_SCHEMAS_FETCH_COMPLETED: {
        this.boxSchemas = action.boxSchemas;
        this.emitChange();
        break;
      }
      case EventConstants.BOX_SCHEMA_DELETE_COMPLETED: {
        this.boxSchemas = this.boxSchemas
          ? this.boxSchemas.filter(({id}) => id !== action.boxSchemaId)
          : null;
        this.emitChange();
        break;
      }
      case EventConstants.BOX_SCHEMA_CREATE_COMPLETED: {
        if (!this.boxSchemas) {
          this.boxSchemas = [action.boxSchema];
        } else {
          this.boxSchemas = reactUpdate(this.boxSchemas, {$push: [action.boxSchema]});
        }
        this.emitChange();
        break;
      }
      case EventConstants.BOX_SCHEMA_UPDATE_COMPLETED: {
        const {boxSchema} = action;
        this._updateStorableSchema(boxSchema);
        this.setSavingState(SavingState.SUCCESS);
        this.emitChange();
        break;
      }
      case EventConstants.BOX_SCHEMAS_UPDATED_FROM_GRAPHQL: {
        const {boxSchemas} = action;
        this.boxSchemas = updateSchemas(this.boxSchemas, boxSchemas);
        this.emitChange();
        break;
      }

      case EventConstants.PLATE_SCHEMAS_FETCH_COMPLETED: {
        this.plateSchemas = action.plateSchemas;
        this.emitChange();
        break;
      }
      case EventConstants.PLATE_SCHEMA_DELETE_COMPLETED: {
        this.plateSchemas = this.plateSchemas
          ? this.plateSchemas.filter(({id}) => id !== action.plateSchemaId)
          : null;
        this.emitChange();
        break;
      }
      case EventConstants.PLATE_SCHEMA_CREATE_COMPLETED: {
        if (!this.plateSchemas) {
          this.plateSchemas = [action.plateSchema];
        } else {
          this.plateSchemas = reactUpdate(this.plateSchemas, {$push: [action.plateSchema]});
        }
        this.emitChange();
        break;
      }
      case EventConstants.PLATE_SCHEMA_UPDATE_COMPLETED: {
        const {plateSchema} = action;
        this._updateStorableSchema(plateSchema);
        this.setSavingState(SavingState.SUCCESS);
        this.emitChange();
        break;
      }
      case EventConstants.PLATE_SCHEMAS_UPDATED_FROM_GRAPHQL: {
        const {plateSchemas} = action;
        this.plateSchemas = updateSchemas(this.plateSchemas, plateSchemas);
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_SCHEMAS_FETCH_COMPLETED: {
        this.containerSchemas = action.containerSchemas;
        this.emitChange();
        break;
      }
      case EventConstants.CONTAINER_SCHEMA_CREATE_COMPLETED: {
        if (!this.containerSchemas) {
          this.containerSchemas = [action.containerSchema];
        } else {
          this.containerSchemas = reactUpdate(this.containerSchemas, {$push: [action.containerSchema]});
        }
        this.emitChange();
        break;
      }
      case EventConstants.CONTAINER_SCHEMA_UPDATE_COMPLETED: {
        const {containerSchema} = action;
        this._updateStorableSchema(containerSchema);
        this.setSavingState(SavingState.SUCCESS);
        this.emitChange();
        break;
      }
      case EventConstants.CONTAINER_SCHEMA_DELETE_COMPLETED: {
        this.containerSchemas = this.containerSchemas
          ? this.containerSchemas.filter(({id}) => id !== action.containerSchemaId)
          : null;
        this.emitChange();
        break;
      }
      case EventConstants.CONTAINER_SCHEMAS_UPDATED_FROM_GRAPHQL: {
        const {containerSchemas} = action;
        this.containerSchemas = updateSchemas(this.containerSchemas, containerSchemas);
        this.emitChange();
        break;
      }

      case EventConstants.LOCATION_SCHEMAS_FETCH_COMPLETED: {
        this.locationSchemas = action.locationSchemas;
        this.emitChange();
        break;
      }
      case EventConstants.LOCATION_SCHEMA_CREATE_COMPLETED: {
        if (!this.locationSchemas) {
          this.locationSchemas = [action.locationSchema];
        } else {
          this.locationSchemas = reactUpdate(this.locationSchemas, {$push: [action.locationSchema]});
        }
        this.emitChange();
        break;
      }
      case EventConstants.LOCATION_SCHEMA_UPDATE_COMPLETED: {
        const {locationSchema} = action;
        this._updateStorableSchema(locationSchema);
        this.setSavingState(SavingState.SUCCESS);
        this.emitChange();
        break;
      }
      case EventConstants.LOCATION_SCHEMA_DELETE_COMPLETED: {
        this.locationSchemas = this.locationSchemas
          ? this.locationSchemas.filter(({id}) => id !== action.locationSchemaId)
          : null;
        this.emitChange();
        break;
      }
      case EventConstants.LOCATION_SCHEMAS_UPDATED_FROM_GRAPHQL: {
        const {locationSchemas} = action;
        this.locationSchemas = updateSchemas(this.locationSchemas, locationSchemas);
        this.emitChange();
        break;
      }

      case EventConstants.BULK_ARCHIVE_STORABLE_SCHEMAS_COMPLETED: {
        const {itemIds, archiveRecordsByItemId} = action;
        itemIds.forEach((itemId: string) => {
          this._archiveStorableSchema(itemId, archiveRecordsByItemId[itemId]);
        });
        this.emitChange();
        break;
      }

      case EventConstants.BULK_UNARCHIVE_STORABLE_SCHEMAS_COMPLETED: {
        const {itemIds} = action;
        itemIds.forEach((itemId: string) => {
          this._unarchiveStorableSchema(itemId);
        });
        this.emitChange();
        break;
      }

      default:
        break;
    }

    return true;
  }
}

const sampleSchemaStore = new SampleSchemaStore(AppDispatcher);
export default sampleSchemaStore;
